import React from 'react';
import PropTypes from 'prop-types';
import Card from 'components/ui/card/Card';
import CardText from 'components/ui/card/CardText';
import FilterContent from './FilterContent';
import compose from 'recompose/compose';
import styled from 'styled-components';
import translate from 'components/hoc/translate';
import utils from 'helpers/utils';
import { FlatButton } from 'material-ui';
import { connect } from 'react-redux';
import { updateFilters, updateFilterCategory } from 'store/actions/listActions';
import theme from 'theme/theme';

const StyledCard = styled(Card)`
    margin-bottom: 30px;

    > div {
        padding-bottom: 0 !important;
    }
`;

const FilterCategoryButtons = styled.div`
    padding: 9px;
    float: right;

    button {
        &:nth-child(2) {
            border-left: 1px solid ${ theme.palette.silver } !important;
        }
    }
`;

const CardHeader = styled.div`
    font-weight: 500;
    box-sizing: border-box;
    position: relative;
    white-space: nowrap;
    background-color: rgba(224, 224, 224, 0.3);
`;

const CardTitle = styled.div`
    padding: 16px;
    font-weight: 500;
    font-size: 20px;
    display: inline-block;
    vertical-align: middle;
    white-space: normal;
`;

class ResourceFilter extends React.Component {
    constructor() {
        super();

        this.mergeFilters = this.mergeFilters.bind(this);
        this.switchCategory = this.switchCategory.bind(this);

        this.debounceUpdateFilterValues = utils.debounce(this.updateFilterValues.bind(this), 300);
        this.throttleUpdateFilterValues = utils.throttle(this.updateFilterValues.bind(this), 1000, { trailing: false });
    }

    switchCategory(newCategory) {
        const {
            activeCategory,
            updateFilterCategory,
            updateQuery,
        } = this.props;

        if (activeCategory === newCategory) {
            return;
        }

        if (utils.isFunction(updateQuery)) {
            updateQuery({
                filterCategory: newCategory,
            });
        }

        updateFilterCategory(newCategory);
    }

    mergeFilters(base, values) {
        let results = Object.assign({}, values);

        utils.each(base, (value, key) => {

            /**
             * Check if the key exists in the base.
             * Then we will merge the resource values with the new values.
             */
            if (results.hasOwnProperty(key)) {
                results[key] = Object.assign(value, results[key]);
                return;
            }
        });

        return results;
    }

    updateFilterValues(newFilters) {
        const {
            filterValues,
            updateListFilters,
            updateQuery,
        } = this.props;

        const payload = {
            filter: this.mergeFilters(filterValues, newFilters),
            offset: 0,
            page: 1,
        };

        if (utils.isFunction(updateQuery)) {
            updateQuery(payload);
        }

        updateListFilters(payload);
    }

    render() {
        const {
            activeCategory,
            filterValues,
            filters,
            resource,
            translate,
        } = this.props;

        if (utils.isEmpty(filters)) {
            return null;
        }

        const title = translate('filters.title');

        const showFilters = !!filters.advanced;

        return (
            <StyledCard>
                <CardHeader>
                    { title &&
                        <CardTitle>{ title }</CardTitle>
                    }

                    {
                        showFilters &&
                        <FilterCategoryButtons>
                            { utils.map(filters, (_, category) => {
                                const isCategory = activeCategory === category;

                                return <FlatButton
                                    disableTouchRipple={ isCategory }
                                    hoverColor={ isCategory ? 'inherit' : 'transparent' }
                                    key={ category }
                                    label={ translate(`filters.category.${ category }`) }
                                    onTouchTap={ () => {
                                        return this.switchCategory(category);
                                    } }
                                    primary={ !isCategory }
                                />;
                            }) }
                        </FilterCategoryButtons>
                    }
                </CardHeader>

                <CardText
                    style={ {
                        padding: '16px',
                    } }
                >
                    <FilterContent
                        category={ activeCategory }
                        filterValues={ filterValues }
                        filters={ filters }
                        resource={ resource }
                        updateFilters={ (newValues) => {
                            if (activeCategory === 'advanced') {
                                this.throttleUpdateFilterValues(newValues);
                            } else {
                                this.debounceUpdateFilterValues(newValues);
                            }
                        } }
                    />
                </CardText>
            </StyledCard>
        );
    }
}

ResourceFilter.defaultProps = {
    filters: {},
};

ResourceFilter.propTypes = {
    activeCategory: PropTypes.string.isRequired,
    filterValues: PropTypes.object,
    filters: PropTypes.object,
    resource: PropTypes.string.isRequired,
    translate: PropTypes.func.isRequired,
    updateFilterCategory: PropTypes.func.isRequired,
    updateListFilters: PropTypes.func.isRequired,
    updateQuery: PropTypes.func,
};

/**
 * @version 09-02-2024 Fixes error when filters prop doesn't include current filterCategory use first key.
 */
const mapStateToProps = (state, props) => {
    const {
        filters,
        resource,
    } = props;
    const stateResource = state[resource];

    return {
        activeCategory: filters[stateResource.filterCategory] ? stateResource.filterCategory : Object.keys(filters)[0],
        filterValues: stateResource.filter,
    };
};

const mapDispatchToProps = (dispatch, props) => {
    const { actionType } = props;

    return {
        updateFilterCategory: (category) => {
            dispatch(updateFilterCategory(actionType, category));
        },
        updateListFilters: (payload) => {
            dispatch(updateFilters(actionType, payload));
        },
    };
};

const enhance = compose(
    connect(
        mapStateToProps,
        mapDispatchToProps,
    ),
    translate,
);

export default enhance(ResourceFilter);



// WEBPACK FOOTER //
// ./src/components/list/Filters/ResourceFilter.js